import { computed } from 'vue'
import {
  colors,
  nativeTypes,
  types,
  sizes,
} from '../components/button/Button.config'

/**
 * @param {Object} context - The context for the component
 * @param {Object} context.props - The props for the component
 * @param {Function} context.emit - The emit function for the component
 * @param {Object} context.button - The button element refference for the component
 * @description A simple mixin to return properties and methods for creating button
 * components.
 * @returns
 */
export function buttonMixin({ props, emit, button }) {
  /**
   * @type {Object}
   * @description A reactive object for the boolean flag to tell the
   * application that this is a button component
   */
  const isButton = computed(
    () => props.tag === 'button' && !props.href && !props.to
  )

  /**
   * @type {Object}
   * @description A reactive object containing the attributes to bind
   * to the button
   */
  const attrs = computed(() => ({
    type: isButton.value ? props.type : null,
    disabled: isButton.value ? props.disabled : null,
    to: !isButton.value ? props.to : null,
  }))

  /**
   * @type {Object}
   * @description A reactive object containing the classes that will be
   * bound to the button
   */
  const classes = computed(() => ({
    [`ak-btn--${props.color}`]: typeof props.size === 'string',
    [`ak-btn--${props.size}`]: typeof props.size === 'string',
    'ak-btn--circle': props.circle,
    'ak-btn--ghost': props.ghost,
    'ak-btn--single-icon': props.singleIcon,
    'ak-btn--full': props.full,
    'is-loading': props.isLoading,
  }))

  /**
   * @type {Object}
   * @description A reactive object to contain the element tag or component
   * that we want to render the button component as
   */
  const computedTag = computed(() => {
    if (props.disabled !== undefined && props.disabled !== false) {
      return 'button'
    }
    return props.tag
  })

  /**
   * @type {Function}
   * @param {Event} e - The click event
   * @description The click handler for the button
   */
  const clicked = (e) => {
    /* istanbul ignore if */
    if (e && e.originalEvent instanceof Event) {
      e.preventDefault()
      e.stopPropagation()
    }
    emit('click', e)
  }

  /**
   * @type {Function}
   * @description A simple alias function to call the button
   * focus method
   */
  const focus = () => {
    button.value.focus()
  }

  return {
    isButton,
    attrs,
    classes,
    computedTag,
    clicked,
    focus,
  }
}

export const buttonProps = {
  /**
   * @type {Object}
   * @prop {Function} validator - Ensures a valid type is used
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description Controls the color of the button component that will
   * be rendered
   */
  loadingColor: {
    validator: /* istanbul ignore next */ (v) => colors.indexOf(v) >= 0,
    type: String,
    default: 'primary',
  },
  /**
   * @type {String}
   * @description This controls whether we render the slot or just
   * output the label, if the label is defined and has a length that
   * will be used, and the slot content will not be rendered.
   */
  label: {
    default: undefined,
    type: String,
  },

  /**
   * @type {Boolean}
   * @description This controls whether we render the ghost style
   * button over the default.
   */
  ghost: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description This controls whether we render the circle style
   * button over the default.
   */
  circle: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description This controls whether we render the as a disabled
   * button or not, this will prevent click handlers from triggering
   * if using the native button element class.
   */

  disabled: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Object}
   * @prop {Function} validator - Ensures a valid type is used
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description Controls the type of the button component that will
   * be rendered i.e submit/reset/button
   */
  type: {
    validator: /* istanbul ignore next */ (v) => nativeTypes.indexOf(v) >= 0,
    type: String,
    default: 'button',
  },

  /**
   * @type {Object}
   * @prop {Function} validator - Ensures a valid type is used
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description Controls the element type of the button component that
   * will be rendered
   */
  tag: {
    validator: /* istanbul ignore next */ (v) => types.indexOf(v) >= 0,
    type: String,
    default: 'button',
  },

  /**
   * @type {String|Object}
   * @description The to prop required for router-link components
   */
  to: {
    type: [String, Object],
    default: undefined,
  },

  /**
   * @type {Object}
   * @prop {Function} validator - Ensures a valid type is used
   * @prop {String} type - The type of the property
   * @description Controls the size of the button component that will
   * be rendered
   */
  size: {
    validator: /* istanbul ignore next */ (v) => sizes.indexOf(v) >= 0,
    type: String,
    default: 'normal',
  },

  /**
   * @type {Boolean}
   * @prop {String} type - The type of the property
   * @description Makes button full width or not
   */
  full: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Object}
   * @prop {Function} validator - Ensures a valid type is used
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description Controls the color of the button component that will
   * be rendered
   */
  color: {
    validator: /* istanbul ignore next */ (v) => colors.indexOf(v) >= 0,
    type: String,
    default: 'primary',
  },

  /**
   * @type {Boolean}
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description Whether its a single icon or not, reduces padding on left
   * and right
   */
  singleIcon: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description Controls whether to display a spinner whilst loading
   */
  isLoading: {
    type: Boolean,
    default: false,
  },
}

export const modalButtonProps = {
  /**
   * @type {String}
   * @description This controls whether we render the slot or just
   * output the label, if the label is defined and has a length that
   * will be used, and the slot content will not be rendered.
   */
  label: {
    default: undefined,
    type: String,
  },

  /**
   * @type {Boolean}
   * @description This controls whether we render the ghost style
   * button over the default.
   */
  ghost: Boolean,

  /**
   * @type {Boolean}
   * @description This controls whether we render the as a disabled
   * button or not, this will prevent click handlers from triggering
   * if using the native button element class.
   */

  disabled: Boolean,

  /**
   * @type {Object}
   * @prop {Function} validator - Ensures a valid type is used
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description Controls the type of the button component that will
   * be rendered i.e submit/reset/button
   */
  type: {
    validator: /* istanbul ignore next */ (v) => nativeTypes.indexOf(v) >= 0,
    type: String,
    default: 'button',
  },

  /**
   * @type {Object}
   * @prop {Function} validator - Ensures a valid type is used
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description Controls the element type of the button component that
   * will be rendered
   */
  tag: {
    validator: /* istanbul ignore next */ (v) => types.indexOf(v) >= 0,
    type: String,
    default: 'button',
  },

  /**
   * @type {String|Object}
   * @description The to prop required for router-link components
   */
  to: {
    type: [String, Object],
    default: undefined,
  },

  /**
   * @type {Object}
   * @prop {Function} validator - Ensures a valid type is used
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description Controls the color of the button component that will
   * be rendered
   */
  color: {
    validator: /* istanbul ignore next */ (v) => colors.indexOf(v) >= 0,
    type: String,
    default: 'primary',
  },

  /**
   * @type {Object}
   * @prop {Function} validator - Ensures a valid type is used
   * @prop {String} type - The type of the property
   * @description Controls the size of the button component that will
   * be rendered
   */
  size: {
    validator: /* istanbul ignore next */ (v) => sizes.indexOf(v) >= 0,
    type: String,
    default: 'large',
  },

  close: {
    default: false,
    type: Boolean,
  },
}
